import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  Alert,
  Modal,
  Form,
  Grid,
  Input,
  InputNumber,
  Select,
  Button,
  Space,
  Tag,
  Spin,
  Divider,
  Typography
} from "antd";
import axios from "axios";
import { useEffect, useRef, useState } from "react";

import { showErrorNotification, showSuccessNotification } from "../../../../utils/notification";
import { clamp } from "../../../../utils/responsiveHelpers";
import {
  abortLatestRequest,
  beginLatestRequest,
  CATALOGUE_INFO_TEXT,
  CATALOGUED_PLATFORM_OPTIONS,
  CONTENT_COMPLETENESS_DISPLAY_CONFIG,
  DETAIL_ORIGIN_DISPLAY_CONFIG,
  EXTERNAL_PROBLEMS_API_BASE,
  FIELD_RENDER_KIND_ENUM,
  reportUnlessCancelled,
  REPEATABLE_FIELD_CONFIG,
  SUB_FIELD_TYPE_ENUM,
  UNSUPPORTED_PLATFORM_PARSE_STATUS,
  URL_REJECTION_MESSAGE_MAP,
  getFieldLabel,
  getFieldNamesForPlatform,
  getFieldRenderKind,
  getSelectFieldOptions,
  PLATFORM_ENUM
} from "../../helper";
import InfoHint, { LabelWithHint } from "../../InfoHint";

const { TextArea } = Input;
const { Text } = Typography;

/** Fields an admin must fill in when creating a problem, and need not when editing one. */
const CREATE_REQUIRED_FIELD_NAMES = Object.freeze(["title"]);

/** Judges whose difficulty label is also required on create, since the judge always publishes one. */
const DIFFICULTY_REQUIRED_PLATFORMS = Object.freeze([PLATFORM_ENUM.LEETCODE_PROBLEM]);

/** The antd input for each field render kind, built from the field's name and label. */
const FIELD_CONTROL_BUILDER_MAP = Object.freeze({
  [FIELD_RENDER_KIND_ENUM.TAG_ARRAY]: ({ label }) => <Select mode="tags" placeholder={`Add ${label.toLowerCase()}`} />,
  [FIELD_RENDER_KIND_ENUM.NUMBER]: ({ label }) => <InputNumber style={{ width: "100%" }} placeholder={label} />,
  [FIELD_RENDER_KIND_ENUM.SELECT]: ({ platform, fieldName, label }) => (
    <Select options={getSelectFieldOptions(platform, fieldName)} placeholder={label} />
  ),
  [FIELD_RENDER_KIND_ENUM.LONG_TEXT]: ({ label }) => <TextArea rows={5} placeholder={label} />,
  [FIELD_RENDER_KIND_ENUM.SHORT_TEXT]: ({ label }) => <Input placeholder={label} />
});

/**
 * A `Form.List` for one repeatable sub-document field (reference solutions,
 * test cases, or samples), rendering each row's sub-fields per `config`.
 *
 * @param {object} props
 * @param {string} props.fieldName
 * @param {import("react").ReactNode} props.label
 * @param {{addButtonLabel: string, subFields: Array<{name: string, label: string, type: string, optional?: boolean}>}} props.config
 */
const RepeatableField = ({ fieldName, label, config }) => {
  const { addButtonLabel, subFields } = config;

  return (
    <Form.Item label={label} style={{ marginBottom: clamp(8, 12) }}>
      <Form.List name={fieldName}>
        {(rowFields, { add, remove }) => (
          <Space direction="vertical" style={{ width: "100%" }}>
            {rowFields.map((rowField) => (
              <Space
                key={rowField.key}
                align="start"
                style={{ width: "100%", display: "flex" }}
                wrap
              >
                {subFields.map((subField) => (
                  <Form.Item
                    key={subField.name}
                    name={[rowField.name, subField.name]}
                    label={subField.label}
                    style={{ minWidth: "min(220px, 100%)", flex: 1, marginBottom: 0 }}
                    rules={
                      subField.optional
                        ? undefined
                        : [{ required: true, message: `${subField.label} is required` }]
                    }
                  >
                    {subField.type === SUB_FIELD_TYPE_ENUM.TEXTAREA ? (
                      <TextArea rows={2} />
                    ) : (
                      <Input />
                    )}
                  </Form.Item>
                ))}
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => remove(rowField.name)}
                />
              </Space>
            ))}
            <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()} block>
              {addButtonLabel}
            </Button>
          </Space>
        )}
      </Form.List>
    </Form.Item>
  );
};

/**
 * One field of the platform-specific form, dispatched to the input that
 * matches its render kind.
 *
 * @param {object} props
 * @param {string} props.platform
 * @param {string} props.fieldName
 * @param {boolean} props.isEditMode
 */
const ProblemField = ({ platform, fieldName, isEditMode }) => {
  const label = getFieldLabel(platform, fieldName);
  const renderKind = getFieldRenderKind(fieldName);
  const isDifficultyRequired =
    fieldName === "difficultyLabel" && DIFFICULTY_REQUIRED_PLATFORMS.includes(platform);
  const isRequired =
    !isEditMode && (CREATE_REQUIRED_FIELD_NAMES.includes(fieldName) || isDifficultyRequired);

  if (renderKind === FIELD_RENDER_KIND_ENUM.REPEATABLE) {
    return (
      <RepeatableField
        fieldName={fieldName}
        label={label}
        config={REPEATABLE_FIELD_CONFIG[fieldName]}
      />
    );
  }

  return (
    <Form.Item
      label={label}
      name={fieldName}
      rules={isRequired ? [{ required: true, message: `${label} is required` }] : undefined}
    >
      {FIELD_CONTROL_BUILDER_MAP[renderKind]({ platform, fieldName, label })}
    </Form.Item>
  );
};

/**
 * What the last pasted link turned out to be: an identity to fill the form
 * with, a record that already exists, or a reason there is nothing to create.
 *
 * @param {object} props
 * @param {object} props.resolvedLink
 * @param {() => void} props.onEdit
 */
const ResolvedLinkNotice = ({ resolvedLink, onEdit }) => {
  const {
    parseStatus,
    platformDisplayName,
    externalId,
    rejectionReason,
    isCreatable,
    existingProblemUniquePublicId,
    existingProblemTitle
  } = resolvedLink;

  if (existingProblemUniquePublicId) {
    return (
      <Alert
        type="warning"
        showIcon
        message={`${platformDisplayName} ${externalId} is already in the catalogue as "${existingProblemTitle}".`}
        description="Editing it is the only way to change what we hold; creating it again would be refused."
        action={
          <Button size="small" type="primary" onClick={onEdit}>
            Edit that problem
          </Button>
        }
      />
    );
  }

  if (isCreatable) {
    return (
      <Alert
        type="success"
        showIcon
        message={`Read as ${platformDisplayName} ${externalId}. Fill in the rest below.`}
      />
    );
  }

  // An unsupported judge has no rejection reason of its own, so its parse status keys the message.
  const rejectionMessageKey = parseStatus === UNSUPPORTED_PLATFORM_PARSE_STATUS ? parseStatus : rejectionReason;

  return (
    <Alert
      type="error"
      showIcon
      message={
        URL_REJECTION_MESSAGE_MAP[rejectionMessageKey] ??
        "That link could not be read as a problem on a judge we catalogue."
      }
    />
  );
};

/**
 * The link step every create starts from: paste the problem's address, and the
 * platform and identifier below fill themselves in.
 *
 * A link the catalogue already holds is reported as such with a way through to
 * editing that record, since creating it again would be refused on the key and
 * the admin would have no way to tell why.
 *
 * @param {object} props
 * @param {object|null} props.resolvedLink - The backend's answer for the last link pasted, null before one is.
 * @param {boolean} props.isResolving
 * @param {(problemUrl: string) => void} props.onResolve
 * @param {() => void} props.onEditResolvedProblem - Switches the form to the record the link already names.
 */
const ProblemUrlStep = ({ resolvedLink, isResolving, onResolve, onEditResolvedProblem }) => (
  <Space direction="vertical" size={8} style={{ width: "100%", marginBottom: 16 }}>
    <LabelWithHint
      label={<Text strong>Problem link</Text>}
      infoText={CATALOGUE_INFO_TEXT.CREATE_FROM_URL}
    />
    <Input.Search
      allowClear
      enterButton="Read link"
      loading={isResolving}
      placeholder="https://codeforces.com/contest/1878/problem/A"
      onSearch={onResolve}
    />
    {resolvedLink && <ResolvedLinkNotice resolvedLink={resolvedLink} onEdit={onEditResolvedProblem} />}
  </Space>
);

/**
 * Create/edit form for one external problem.
 *
 * A create starts from the problem's own link rather than a typed identifier.
 * The link is parsed by the same code that matches a student's paste later, so
 * the record lands under the key that paste will look up; typing the parts by
 * hand is what produced records nothing ever resolved against. A link already
 * in the catalogue switches the form into editing that record instead of
 * creating a duplicate the unique key would refuse.
 *
 * The rendered fields depend entirely on the resolved platform, since a union
 * of every platform's fields fails validation on submit. `prefill` (a
 * reconciliation-queue row, whose link the queue already parsed) skips the link
 * step. `uniquePublicId` opens straight into editing an existing record.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {string|null} [props.uniquePublicId] - Set to edit an existing problem.
 * @param {{platform: string, externalId: string, externalUrl?: string}|null} [props.prefill] -
 *   Known identity for a brand-new problem, from a reconciliation-queue row.
 * @param {() => void} props.onClose
 * @param {() => void} props.onSaved - Called after a successful create or update.
 * @param {string} props.ROOT_URL
 */
const ProblemFormModal = ({
  open,
  uniquePublicId = null,
  prefill = null,
  onClose,
  onSaved,
  ROOT_URL
}) => {
  const { platform: prefillPlatform = null } = prefill ?? {};

  const [editingUniquePublicId, setEditingUniquePublicId] = useState(uniquePublicId);
  const [selectedPlatform, setSelectedPlatform] = useState(prefillPlatform);
  const [problemMeta, setProblemMeta] = useState(null);
  const [resolvedLink, setResolvedLink] = useState(null);
  const [isResolving, setIsResolving] = useState(false);
  const [isFetchingProblem, setIsFetchingProblem] = useState(Boolean(uniquePublicId));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const resolveAbortControllerRef = useRef(null);
  const fetchProblemAbortControllerRef = useRef(null);
  const [form] = Form.useForm();
  const screens = Grid.useBreakpoint();

  const isMobileView = screens.md === false;
  const isEditMode = Boolean(editingUniquePublicId);
  const fieldNames = selectedPlatform ? getFieldNamesForPlatform(selectedPlatform) : [];
  const isIdentitySettled = isEditMode || Boolean(prefill) || Boolean(selectedPlatform);
  // An edit saves only once the record has loaded, so a form still empty or whose load failed cannot overwrite it.
  const isSubmitDisabled = !isIdentitySettled || (isEditMode && !problemMeta);
  const { contentFidelity, isManuallyCurated } = problemMeta ?? {};
  const { label: completenessLabel, color: completenessColor } =
    CONTENT_COMPLETENESS_DISPLAY_CONFIG[contentFidelity] ?? {};
  const { label: detailOriginLabel, color: detailOriginColor } = isManuallyCurated
    ? DETAIL_ORIGIN_DISPLAY_CONFIG.ADMIN_ENTERED
    : DETAIL_ORIGIN_DISPLAY_CONFIG.IMPORTED;
  const { label: selectedPlatformLabel } =
    CATALOGUED_PLATFORM_OPTIONS.find(({ value }) => value === selectedPlatform) ?? {};

  /**
   * Reads the platform and the problem's own identifier out of a pasted link,
   * and prefills every field they settle.
   *
   * @param {string} problemUrl - Exactly what the admin pasted.
   */
  const handleResolveUrl = (problemUrl) => {
    if (!problemUrl.trim()) {
      return;
    }

    const { signal } = beginLatestRequest(resolveAbortControllerRef);

    setIsResolving(true);
    axios
      .post(`${ROOT_URL}/${EXTERNAL_PROBLEMS_API_BASE}/resolve-url`, { problemUrl }, { signal })
      .then((response) => {
        const resolveResponse = response.data;
        const { platform, externalId, normalizedUrl, isCreatable } = resolveResponse;

        setResolvedLink(resolveResponse);
        if (!isCreatable) {
          setSelectedPlatform(null);
          return;
        }

        setSelectedPlatform(platform);
        form.setFieldsValue({ platform, externalId, externalUrl: normalizedUrl });
      })
      .catch(reportUnlessCancelled)
      .finally(() => {
        if (!signal.aborted) {
          setIsResolving(false);
        }
      });
  };

  /** Switches to editing the record the pasted link already resolves to. */
  const handleEditResolvedProblem = () => {
    const { existingProblemUniquePublicId } = resolvedLink;

    form.resetFields();
    setResolvedLink(null);
    setIsFetchingProblem(true);
    setEditingUniquePublicId(existingProblemUniquePublicId);
  };

  /**
   * Builds the request body from the current form values, keeping only the
   * fields the selected platform actually declares. Identity fields
   * (platform, externalId) are handled separately: the create payload adds
   * them back, and an edit never sends them at all.
   *
   * @param {Record<string, any>} formValues
   * @returns {Record<string, any>} Only the fields the platform declares and the form set.
   */
  const buildPlatformFieldsPayload = (formValues) => {
    const platformFieldNames = getFieldNamesForPlatform(selectedPlatform);
    return Object.fromEntries(
      platformFieldNames
        .filter((fieldName) => formValues[fieldName] !== undefined)
        .map((fieldName) => [fieldName, formValues[fieldName]])
    );
  };

  /**
   * Creates or updates the problem from the form values, depending on edit
   * mode, then reports the save through `onSaved`.
   *
   * @param {Record<string, any>} formValues - Platform fields, plus `externalId` on a create.
   */
  const handleFinish = (formValues) => {
    const { externalId, ...restFormValues } = formValues;
    const platformFieldsPayload = buildPlatformFieldsPayload(restFormValues);

    setIsSubmitting(true);
    const saveRequest = isEditMode
      ? axios.patch(
        `${ROOT_URL}/${EXTERNAL_PROBLEMS_API_BASE}/${editingUniquePublicId}`,
        platformFieldsPayload
      )
      : axios.post(`${ROOT_URL}/${EXTERNAL_PROBLEMS_API_BASE}`, {
        platform: selectedPlatform,
        externalId,
        ...platformFieldsPayload
      });

    saveRequest
      .then(() => {
        showSuccessNotification(`Problem ${isEditMode ? "updated" : "created"} successfully!`);
        onSaved();
      })
      .catch(showErrorNotification)
      .finally(() => setIsSubmitting(false));
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    form.resetFields();
    setProblemMeta(null);
    setResolvedLink(null);
    setEditingUniquePublicId(uniquePublicId);
    setSelectedPlatform(uniquePublicId ? null : prefillPlatform);
    if (!uniquePublicId && prefill) {
      form.setFieldsValue(prefill);
    }
  }, [open, uniquePublicId, prefill, prefillPlatform, form]);

  useEffect(() => {
    if (!open || !editingUniquePublicId) {
      return;
    }

    const { signal } = beginLatestRequest(fetchProblemAbortControllerRef);

    setIsFetchingProblem(true);
    axios
      .get(`${ROOT_URL}/${EXTERNAL_PROBLEMS_API_BASE}/${editingUniquePublicId}`, { signal })
      .then((response) => {
        const { platform, ...fields } = response.data;
        setSelectedPlatform(platform);
        setProblemMeta(fields);
        form.setFieldsValue({ ...fields, platform });
      })
      .catch(reportUnlessCancelled)
      .finally(() => {
        if (!signal.aborted) {
          setIsFetchingProblem(false);
        }
      });
  }, [open, editingUniquePublicId, form, ROOT_URL]);

  useEffect(
    () => () => {
      abortLatestRequest(resolveAbortControllerRef);
      abortLatestRequest(fetchProblemAbortControllerRef);
    },
    []
  );

  return (
    <Modal
      title={isEditMode ? "Edit External Problem" : "Create External Problem"}
      open={open}
      onCancel={onClose}
      width={isMobileView ? "94%" : 760}
      styles={{
        content: { backgroundColor: "var(--component-bg)" },
        body: { maxHeight: "70vh", overflowY: "auto" }
      }}
      footer={[
        <Button key="back" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isSubmitting}
          disabled={isSubmitDisabled}
          onClick={() => form.submit()}
        >
          {isEditMode ? "Save Changes" : "Create Problem"}
        </Button>
      ]}
    >
      <Spin spinning={isFetchingProblem}>
        {problemMeta && (
          <Space style={{ marginBottom: 16 }}>
            <Tag color={completenessColor}>{completenessLabel}</Tag>
            <Tag color={detailOriginColor}>{detailOriginLabel}</Tag>
            <InfoHint title={CATALOGUE_INFO_TEXT.DETAIL_ORIGIN} />
          </Space>
        )}

        {!isEditMode && !prefill && (
          <ProblemUrlStep
            resolvedLink={resolvedLink}
            isResolving={isResolving}
            onResolve={handleResolveUrl}
            onEditResolvedProblem={handleEditResolvedProblem}
          />
        )}

        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Form.Item
            label="Platform"
            name="platform"
            rules={[{ required: true, message: "Platform is required" }]}
          >
            <Select options={CATALOGUED_PLATFORM_OPTIONS} disabled />
          </Form.Item>

          <Form.Item
            label="External ID"
            name="externalId"
            rules={[{ required: true, message: "External ID is required" }]}
          >
            <Input disabled placeholder="Read from the problem's link" />
          </Form.Item>

          {selectedPlatform && (
            <>
              <Divider orientation="left" style={{ marginTop: 0 }}>
                {selectedPlatformLabel} Fields
              </Divider>

              {fieldNames.map((fieldName) => (
                <ProblemField
                  key={fieldName}
                  platform={selectedPlatform}
                  fieldName={fieldName}
                  isEditMode={isEditMode}
                />
              ))}
            </>
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

export default ProblemFormModal;
